/* eslint-disable consistent-return */
/* eslint-disable no-use-before-define */
/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2023. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * SD-1067 - RC - Implemented custom function to call the address validation
 * REST API which is invoked from the IEG combo box.
 */
const buildAddressEntity = (street1, street2, city, state, zipCode) => ({
  street1,
  street2,
  city,
  state,
  zipCode,
});

let isDoingSearch = false;

/**
 * Builds a new Address Item
 *
 * @param {*} fullAddress - Full address
 * @param {*} city    - Address city
 * @param {*} zipCode - Address zipcode
 * @returns an AddressItem
 */
const buildAddressItem = (id, fullAddress, street1, street2, city, state, zipCode) => ({
  id,
  value: fullAddress,
  item: { ...buildAddressEntity(street1, street2, city, state, zipCode) },
});

/**
 * Invokes a Rest API to retrieve the address information.
 *
 * @param {*} query - The address to search for.
 * @returns an array of  Address Items
 */
async function getAddresses(query) {
  isDoingSearch = true;
  const items = [];

  const instanceKey = sessionStorage.getItem('instanceKey') || 'null';
  try {
    const url = `${process.env.REACT_APP_REST_URL}/v1/ua/sd_ua_address_validation/${query}/instancekey/${instanceKey}`;
    const response = await fetch(url);

    return response.json().then(results => {
      // Set instance key value based on return
      if (
        results.instanceKey &&
        String(results.instanceKey).valueOf() !==
          String(sessionStorage.getItem('instanceKey')).valueOf()
      ) {
        sessionStorage.setItem('instanceKey', results.instanceKey);
      } else if (sessionStorage.getItem('instanceKey')) sessionStorage.removeItem('instanceKey');

      // set list of addresses
      if (results.dtls) {
        results.dtls.forEach(result => {
          if (
            result !== undefined &&
            result.zipCode !== undefined &&
            result.stateCode !== undefined &&
            result.streetLine1 !== undefined
          ) {
            items.push(
              buildAddressItem(
                result.streetLine1,
                result.addressData,
                result.streetLine1,
                result.streetLine2,
                result.city,
                result.stateCode.value,
                result.zipCode
              )
            );
          }
        });
      }
      isDoingSearch = false;
      return items;
    });
  } catch {
    isDoingSearch = false;
    return items;
  }
}

/**
 * Custom function for ComboBox element, it searches Addresses.
 *
 * @param {String} addressData
 * @returns Array of address combinations matching the addressData.
 */
async function searchAddressCombo(addressData, addressEntity) {
  if (isDoingSearch) {
    return;
  }
  const items = await getAddresses(addressData).then(data => ({ items: data }));
  if (items.items.length > 0) {
    // BEGIN : SD-12819 VR : Fix PMR state field issue : Removed the IEG target entity
    // to supress the generated js and overriden with the below custom code to implement
    // the same OOTB functionality and fix the state combo box issue as well.

    const docId =
      addressEntity === 'Residential'
        ? '[id$="SDAddressComboBox.fullAddress"]'
        : '[id$="SDMailingAddressComboBox.fullMailingAddress"]';
    const docEleList = document.querySelectorAll(docId);
    let docEle;

    // Get the matching DIV element
    for (const node of docEleList) {
      if (node.nodeName.toUpperCase() === 'DIV') {
        docEle = node;
      }
    }

    if (docEle) {
      // Use the span for tracking the onChange event
      const spanElement = docEle.getElementsByClassName('wds-c-combo-box__input-wrapper').item(0);

      /**
       * Inner function used for disabling address fields and setting display of state field
       */
      const setAddressFunc = () => {
        const stateEle = document.querySelector(`[id$="${addressEntity}Address.state"]`);
        const street1Ele = document.querySelector(`[id$="${addressEntity}Address.street1"]`);
        const street2Ele = document.querySelector(`[id$="${addressEntity}Address.street2"]`);
        const cityEle = document.querySelector(`[id$="${addressEntity}Address.city"]`);
        const zipEle = document.querySelector(`[id$="${addressEntity}Address.zipCode"]`);

        if (stateEle) {
          // Timeout of 1ms is need, otherwise the element doesn't refresh the display
          setTimeout(() => {
            // Use the selected address to set the state display
            const addressVal = spanElement
              .getElementsByClassName('wds-c-combo-box__token-span')
              .item(0)
              .textContent.split(' ');
            if (addressVal.length > 2) {
              // Address is in the form of
              // xxx street1 street2, city, state zipcode
              // second to last item split using " " will be the state
              const stateVal = addressVal[addressVal.length - 2];
              stateEle.setAttribute('value', stateVal);
              stateEle.value = stateVal;
            }
            stateEle.disabled = true;
          }, 1);
        }
        if (street1Ele) {
          street1Ele.disabled = true;
        }
        if (street2Ele) {
          street2Ele.disabled = true;
        }
        if (zipEle) {
          zipEle.disabled = true;
        }
        if (cityEle) {
          cityEle.disabled = true;
        }
        sessionStorage.removeItem('instanceKey');
        // remove this event after it has triggered
        spanElement.removeEventListener('change', setAddressFunc);
      };
      // add event when we start to search
      spanElement.addEventListener('change', setAddressFunc);
    }
  }

  // END : SD-12819 VR : Remove this block when curam OOTB issue is fixed
  return {
    // Removing the 'Enter Manually' option from returned list
    newItem: {
      id: '-1',
      label: `Can't find your address? Enter manually..`,
      value: '',
      position: 'bottom',
    },
    items: items.items,
  };
}

/**
 * Event callback used to re-enable address fields if the address textbox is cleared.
 * @param {*} addressEntity
 */
function addressChangeEventHelper(addressEntity) {
  const docId =
    addressEntity === 'Residential'
      ? '[id$="SDAddressComboBox.fullAddress"]'
      : '[id$="SDMailingAddressComboBox.fullMailingAddress"]';
  const docEleList = document.querySelectorAll(docId);
  let docEle;
  sessionStorage.removeItem('instanceKey');
  for (const node of docEleList) {
    if (node.nodeName.toUpperCase() === 'DIV') {
      docEle = node;
    }
  }

  let temp;
  if (docEle) {
    temp = docEle.getElementsByClassName('wds-c-combo-box__input-wrapper');
  }

  let temp2;
  if (temp && temp.length > 0) {
    temp2 = temp[0].getElementsByClassName('wds-c-combo-box__token-span');
  }
  let temp3;
  if (temp2 && temp2.length > 0) {
    temp3 = temp2.item(0);
  }

  if (!temp3 || !temp3.textContent) {
    const stateEle = document.querySelector(`[id$="${addressEntity}Address.state"]`);
    const street1Ele = document.querySelector(`[id$="${addressEntity}Address.street1"]`);
    const street2Ele = document.querySelector(`[id$="${addressEntity}Address.street2"]`);
    const cityEle = document.querySelector(`[id$="${addressEntity}Address.city"]`);
    const zipEle = document.querySelector(`[id$="${addressEntity}Address.zipCode"]`);

    if (stateEle) {
      stateEle.disabled = false;
    }
    if (street1Ele) {
      street1Ele.disabled = false;
    }
    if (street2Ele) {
      street2Ele.disabled = false;
    }
    if (zipEle) {
      zipEle.disabled = false;
    }
    if (cityEle) {
      cityEle.disabled = false;
    }
  }
}

/** \
 * Wrapper function used to pass in params to actual event
 */
function residentialAddressChangeEvent() {
  document.removeEventListener('click', mailingAddressChangeEvent);
  addressChangeEventHelper('Residential');
}

/**
 * rapper function used to pass in params to actual event
 */
function mailingAddressChangeEvent() {
  document.removeEventListener('click', residentialAddressChangeEvent);
  addressChangeEventHelper('Mailing');
}

/**
 * Function wrapper for passing params into searchbox function
 * @param {*} addressData
 * @returns
 */
async function searchMailingAddressCombo(addressData) {
  document.addEventListener('click', mailingAddressChangeEvent);
  return searchAddressCombo(addressData, 'Mailing');
}

/**
 * Function wrapper for passing params into searchbox function
 * @param {*} addressData
 * @returns
 */
async function searchResidentialAddressCombo(addressData) {
  document.addEventListener('click', residentialAddressChangeEvent);
  return searchAddressCombo(addressData, 'Residential');
}

export { searchMailingAddressCombo, searchResidentialAddressCombo };
